"use client"

import type { CSSProperties } from "react"

import { useIsMobile } from "../hooks/use-mobile"
import { useSidebarReflowZoom } from "../hooks/use-sidebar-reflow-zoom"
import { cn } from "./utils"

export type FloatingSheetSide = "top" | "right" | "bottom" | "left"

/** Desktop rail — narrow floating panel beside the hub. */
export const FLOATING_SHEET_DESKTOP_WIDTH =
  "w-[min(20rem,calc(100vw-1rem))] max-w-none sm:max-w-none"

/** Mobile / ≥200% zoom — nearly full viewport; `!` beats SheetContent default `w-3/4`. */
export const FLOATING_SHEET_COMPACT_WIDTH =
  "!w-[calc(100vw-1rem)] max-w-none sm:max-w-none"

export const FLOATING_SHEET_CHROME_CLASS =
  "flex flex-col border border-border bg-background shadow-[var(--shadow-sheet-panel)] rounded-xl overflow-hidden p-0 gap-0"

export function floatingSheetWidthClass(compact: boolean): string {
  return compact ? FLOATING_SHEET_COMPACT_WIDTH : FLOATING_SHEET_DESKTOP_WIDTH
}

/** True when floating sheets should expand to nearly full viewport width. */
export function useCompactFloatingSheet(): boolean {
  const isMobile = useIsMobile()
  const reflowZoom = useSidebarReflowZoom()
  return isMobile || reflowZoom
}

/**
 * Inset + sizing for floating product sheets (Export, Properties, invite, folder create):
 * `Sheet` + `showOverlay={false}` — no dimming layer; panel is inset with rounded corners.
 */
export function getFloatingSheetInsetProps(
  side: FloatingSheetSide,
  compact = false,
): {
  side: FloatingSheetSide
  className: string
  style: CSSProperties
} {
  const widthClass = floatingSheetWidthClass(compact)

  switch (side) {
    case "right":
      return {
        side: "right",
        className: cn(FLOATING_SHEET_CHROME_CLASS, widthClass),
        style: {
          top: "0.5rem",
          bottom: "0.5rem",
          right: "0.5rem",
          left: "auto",
          height: "calc(100svh - 1rem)",
        },
      }
    case "left":
      return {
        side: "left",
        className: cn(FLOATING_SHEET_CHROME_CLASS, widthClass),
        style: {
          top: "0.5rem",
          bottom: "0.5rem",
          left: "0.5rem",
          right: "auto",
          height: "calc(100svh - 1rem)",
        },
      }
    case "bottom":
      return {
        side: "bottom",
        className: cn(FLOATING_SHEET_CHROME_CLASS, "max-h-[min(80vh,32rem)]", widthClass),
        style: {
          left: "0.5rem",
          right: "0.5rem",
          bottom: "0.5rem",
          top: "auto",
          height: "auto",
        },
      }
    case "top":
      return {
        side: "top",
        className: cn(FLOATING_SHEET_CHROME_CLASS, "max-h-[min(80vh,32rem)]", widthClass),
        style: {
          left: "0.5rem",
          right: "0.5rem",
          top: "0.5rem",
          bottom: "auto",
          height: "auto",
        },
      }
  }
}
